import { Capacitor } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { client } from './api'
import { absoluteUrl } from './apiBase'

export function isNativeApp(): boolean {
  if (Capacitor.isNativePlatform()) return true
  // Bundled shell with native bridge but platform reported as web (legacy remote server.url)
  return Capacitor.isPluginAvailable('App') && Capacitor.getPlatform() !== 'web'
}

export function nativePlatform(): 'android' | 'ios' | null {
  if (!isNativeApp()) return null
  const p = Capacitor.getPlatform()
  if (p === 'android' || p === 'ios') return p
  return null
}

export function markNativeShell(): void {
  if (isNativeApp()) {
    document.documentElement.classList.add('native-app')
  }
}

export type OAuthResult = { dest?: string; error?: string }

type OAuthCompleteListener = (result: OAuthResult) => void

let pendingOAuth: ((result: OAuthResult) => void) | null = null
let cancelTimer: number | undefined
let oauthListenersReady = false
let oauthCompleteListener: OAuthCompleteListener | null = null

/** App.tsx registers this to finish sign-in after cold-start deep links. */
export function onNativeOAuthComplete(listener: OAuthCompleteListener): () => void {
  oauthCompleteListener = listener
  return () => {
    if (oauthCompleteListener === listener) oauthCompleteListener = null
  }
}

/** Chrome Custom Tabs hand off via intent:// — normalize to pairvelle:// for parsing. */
function normalizeOAuthUrl(raw: string): string {
  if (raw.startsWith('intent://')) {
    const rest = raw.slice('intent://'.length).split('#Intent')[0] ?? ''
    return `pairvelle://${rest}`
  }
  return raw
}

function parseOAuthReturn(raw: string): { code?: string; error?: string } | null {
  const normalized = normalizeOAuthUrl(raw)
  let parsed: URL
  try {
    parsed = new URL(normalized)
  } catch {
    return null
  }
  if (parsed.protocol === 'pairvelle:' && parsed.hostname === 'auth') {
    return {
      code: parsed.searchParams.get('code') ?? undefined,
      error: parsed.searchParams.get('error') ?? undefined,
    }
  }
  if (
    (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
    parsed.hostname === 'pairvelle.com' &&
    parsed.pathname === '/auth/app-return'
  ) {
    return {
      code: parsed.searchParams.get('code') ?? undefined,
      error: parsed.searchParams.get('error') ?? undefined,
    }
  }
  return null
}

function deliverOAuthResult(result: OAuthResult) {
  if (pendingOAuth) {
    const finish = pendingOAuth
    pendingOAuth = null
    finish(result)
    return
  }
  oauthCompleteListener?.(result)
}

async function exchangeOAuthCode(code: string): Promise<OAuthResult> {
  try {
    const res = await client.exchangeOAuthCode(code)
    return { dest: res.dest }
  } catch (e) {
    return { error: (e as Error).message }
  }
}

async function handleOAuthReturn(raw: string): Promise<boolean> {
  const parsed = parseOAuthReturn(raw)
  if (!parsed) return false

  if (cancelTimer) {
    window.clearTimeout(cancelTimer)
    cancelTimer = undefined
  }

  void Browser.close().catch(() => undefined)

  if (parsed.error) {
    deliverOAuthResult({ error: parsed.error })
    return true
  }

  const code = parsed.code
  if (!code) {
    deliverOAuthResult({ error: 'missing_code' })
    return true
  }

  const result = await exchangeOAuthCode(code)
  deliverOAuthResult(result)
  return true
}

/** Register once at boot — handles cold-start deep links when Custom Tabs reopen the app. */
export function initNativeOAuthListener(): void {
  if (!isNativeApp() || oauthListenersReady) return
  oauthListenersReady = true

  void App.addListener('appUrlOpen', (event) => {
    void handleOAuthReturn(event.url)
  })

  void App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) return
    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) void handleOAuthReturn(launch.url)
    })
  })

  void App.getLaunchUrl().then((launch) => {
    if (launch?.url) void handleOAuthReturn(launch.url)
  })
}

/** Google OAuth in Custom Tabs, then deep-link back into the WebView with a one-time exchange code. */
export async function startNativeGoogleAuth(authUrl: string): Promise<OAuthResult> {
  initNativeOAuthListener()
  const url = absoluteUrl(authUrl)

  return new Promise((resolve) => {
    if (pendingOAuth) {
      resolve({ error: 'Sign-in already in progress' })
      return
    }

    let finishedListener: PluginListenerHandle | undefined
    let browserOpened = false
    let settled = false

    const finish = (result: OAuthResult) => {
      if (settled) return
      settled = true
      pendingOAuth = null
      if (cancelTimer) {
        window.clearTimeout(cancelTimer)
        cancelTimer = undefined
      }
      void finishedListener?.remove()
      void Browser.close().catch(() => undefined)
      resolve(result)
    }

    pendingOAuth = finish

    void (async () => {
      finishedListener = await Browser.addListener('browserFinished', () => {
        if (!browserOpened) return
        if (cancelTimer) window.clearTimeout(cancelTimer)
        cancelTimer = window.setTimeout(() => {
          if (pendingOAuth === finish) finish({ error: 'access_denied' })
        }, 8000)
      })

      try {
        await Browser.open({ url })
        browserOpened = true
      } catch (e) {
        finish({ error: (e as Error).message || 'Could not open sign-in window' })
      }
    })()
  })
}
